import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, FileSpreadsheet } from "lucide-react";
import ReportDrawer from "@/components/ReportDrawer";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";

const monthName = (m) => new Date(2000, m - 1, 1).toLocaleString("en", { month: "long" });

export default function Monthly() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [drawerReport, setDrawerReport] = useState(null);

  useEffect(() => {
    api.get("/dashboard/monthly", { params: { year, month } }).then((r) => setData(r.data));
  }, [year, month]);

  const shift = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const openCell = async (cell) => {
    if (cell.status !== "submitted" || !cell.report_id) return;
    const { data } = await api.get(`/reports/${cell.report_id}`);
    setDrawerReport(data);
  };

  const exportExcel = () => {
    const token = localStorage.getItem("eod_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/monthly.xlsx?year=${year}&month=${month}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `eod-compliance-${year}-${String(month).padStart(2, "0")}.xlsx`;
        a.click();
        toast.success("Excel downloaded");
      });
  };

  const days = data?.days || [];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Monthly Compliance</div>
          <h1 className="font-display text-3xl font-bold">{monthName(month)} {year}</h1>
          <div className="text-sm text-zinc-400 mt-1">Green = submitted · Red = missed · Grey = non-working · Dashed = pending</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="monthly-prev-button"
            className="bg-[#121215] border-[#27272a]"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="monthly-next-button"
            className="bg-[#121215] border-[#27272a]"><ChevronRight className="w-4 h-4" /></Button>
          <Button onClick={exportExcel} data-testid="monthly-export-button"
            className="bg-emerald-500/90 hover:bg-emerald-500 text-white">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      <Card data-testid="monthly-heatmap-grid" className="bg-[#121215] border-[#27272a] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#27272a] bg-[#0f0f12]">
                <th className="sticky left-0 bg-[#0f0f12] text-left p-3 text-xs uppercase tracking-wider text-zinc-500 min-w-[200px]">Manager</th>
                {days.map((d) => {
                  const dt = new Date(d);
                  const wd = dt.toLocaleString("en", { weekday: "short" })[0];
                  return (
                    <th key={d} className="p-1 text-center font-mono text-[10px] text-zinc-500 w-7">
                      <div>{dt.getDate()}</div>
                      <div className="text-zinc-600">{wd}</div>
                    </th>
                  );
                })}
                <th className="p-3 text-right text-xs uppercase tracking-wider text-zinc-500">Rate</th>
              </tr>
            </thead>
            <tbody>
              <TooltipProvider delayDuration={100}>
                {(data?.grid || []).map((row) => (
                  <tr key={row.manager.id} className="border-b border-[#1f1f23] hover:bg-[#18181b]">
                    <td className="sticky left-0 bg-[#121215] p-3 min-w-[200px]">
                      <div className="font-medium">{row.manager.name}</div>
                      <div className="text-xs text-zinc-500">{row.manager.role_label}</div>
                    </td>
                    {row.cells.map((c) => (
                      <td key={c.date} className="p-1 text-center align-middle">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button onClick={() => openCell(c)}
                              className={`hm-cell hm-${c.status}`}
                              data-testid={`heatmap-cell-${row.manager.id}-${c.date}`}
                              aria-label={`${row.manager.name} ${c.date} ${c.status}`}
                            />
                          </TooltipTrigger>
                          <TooltipContent className="bg-[#121215] border-[#27272a] text-zinc-100">
                            <div className="text-xs font-medium">{c.date}</div>
                            <div className="text-xs text-zinc-400 capitalize">{c.status}</div>
                          </TooltipContent>
                        </Tooltip>
                      </td>
                    ))}
                    <td className="p-3 text-right">
                      <Badge variant="outline"
                        className={`border-[#3f3f46] font-mono ${row.compliance_pct >= 90 ? "text-emerald-300" : row.compliance_pct >= 70 ? "text-amber-300" : "text-rose-300"}`}>
                        {row.submitted}/{row.working_days} · {row.compliance_pct}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </TooltipProvider>
              {(!data?.grid || data.grid.length === 0) && (
                <tr><td colSpan={days.length + 2} className="p-8 text-center text-zinc-500">No managers yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <ReportDrawer open={!!drawerReport} onOpenChange={(o) => !o && setDrawerReport(null)} report={drawerReport} />
    </div>
  );
}
